import { FC } from 'react';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  IconButton,
  FormLabel
} from '@mui/material';
import { ExpandMore, Visibility, Description, Map } from '@mui/icons-material';
import { IBuild } from '../../../models';

type FieldDescriptor = {
  name: string;
  accessor: string;
  Cell?: (value: { cell: { row: { original: IBuild } } }) => JSX.Element;
};

interface IMobileMyBuildsProps {
  original: IBuild;
  handleView?: (val: IBuild) => void;
  handleDownloadSitePlan?: (val: IBuild) => void;
  showSitePlan?: (val: IBuild) => boolean;
  handleDownloadContract?: (val: IBuild) => void;
  showContract?: (val: IBuild) => boolean;
  fields?: FieldDescriptor[];
}

export const MobileMyBuilds: FC<IMobileMyBuildsProps> = ({
  original,
  handleView,
  handleDownloadSitePlan,
  showSitePlan,
  handleDownloadContract,
  showContract,
  fields
}) => {
  const classes = useStyles();
  const { neighborhoodName } = original;
  const canShowSitePlan = handleDownloadSitePlan && (showSitePlan ? showSitePlan(original) : true);
  const canShowContract = handleDownloadContract && (showContract ? showContract(original) : true);

  return (
    <Accordion className={classes.root}>
      <AccordionSummary className={classes.accordion} expandIcon={<ExpandMore />}>
        <div className={classes.topPanelSummaryWrapper}>
          <Typography variant="body2">{neighborhoodName}</Typography>
          <div className={classes.buttonsWrapper}>
            {canShowSitePlan && (
              <IconButton
                className={classes.iconButton}
                color="primary"
                onClick={e => {
                  e.stopPropagation();
                  handleDownloadSitePlan?.(original);
                }}
                size="large"
              >
                <Map />
              </IconButton>
            )}
            {canShowContract && (
              <IconButton
                className={classes.iconButton}
                color="secondary"
                onClick={e => {
                  e.stopPropagation();
                  handleDownloadContract?.(original);
                }}
                size="large"
              >
                <Description />
              </IconButton>
            )}
            {handleView && (
              <IconButton
                className={classes.iconButton}
                color="primary"
                onClick={e => {
                  e.stopPropagation();
                  handleView(original);
                }}
                size="large"
              >
                <Visibility />
              </IconButton>
            )}
          </div>
        </div>
      </AccordionSummary>
      <AccordionDetails className={classes.details}>
        {fields?.map((field, index) => (
          <div key={`${index}`} className={classes.panelSummaryWrapper}>
            <FormLabel
              component="span"
              className={clsx(classes.labelColor, classes.boldName, classes.subLabel)}
            >
              {field?.name?.length > 1 ? `${field.name}:` : field.name}
            </FormLabel>
            <Typography component="span" variant="body2" style={{ paddingTop: '4px' }}>
              {typeof field.Cell === 'function'
                ? field.Cell({ cell: { row: { original } } })
                : // fallback to direct accessor if provided
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  (original as any)[field.accessor] ?? '--'}
            </Typography>
          </div>
        ))}
      </AccordionDetails>
    </Accordion>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    maxWidth: '100%',
    border: `1px solid ${theme.palette.grey[300]}`,
    overflowX: 'hidden'
  },
  accordion: {
    padding: '0 16px',
    '&& .MuiAccordionSummary-expandIcon': {
      padding: 3
    }
  },
  topPanelSummaryWrapper: {
    marginTop: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    '&:first-child': { marginTop: 0 }
  },
  details: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    padding: '1.125rem 1rem',
    backgroundColor: theme.palette.grey[100]
  },
  panelSummaryWrapper: {
    display: 'flex',
    padding: 0,
    '&:first-child': { marginTop: 0 }
  },
  iconButton: {
    padding: `2px 5px`
  },
  buttonsWrapper: {
    display: 'flex'
  },
  boldName: {
    fontWeight: 600
  },
  subLabel: {
    marginRight: 10,
    paddingTop: theme.spacing(0.25),
    paddingBottom: theme.spacing(0.25),
    fontSize: theme.typography.body2.fontSize
  },
  labelColor: {
    color: theme.palette.typography.labels
  }
}));
